// 遠端協助的前端 mock（只在 KMS 操作台 mock = `all` 時接，../mockMode.ts）：kmgr 沒部署的環境看畫面用。
// 主機沿用 ../mock.ts 的假主機（GET …/targets?kind=agent）；狀態只放記憶體（重新整理就沒了）。
// 模擬的流程：開始後 1 秒「遠端工具已開啟」（launched）、4 秒螢幕前的人按允許（active）；加權限 3 秒後允許；
// 主機名稱含 LOCK 的回螢幕鎖定並等解鎖（3 秒後「解鎖」、跳允許視窗，另有 1 個登入的工作階段；M4-06b）、含 NOUSER 的回沒人登入
// （REMOTE_NOT_ALLOWED）。形狀照 kmgr fc3f36e + M4-06b（…/launch、GET /v1/me/kmgr/remote-sessions 的 access）。
import { KinioError } from '@kinio/web-sdk';
import { kmgrMock, type MockWorld } from '../mock';
import type { WireTargetList } from '../wire';
import type { AssistRequest, RemoteEvent, RemoteSession } from './types';

interface MockSession {
  s: RemoteSession;
  events: RemoteEvent[];
  startedMs?: number;
  lockedMs?: number;
  upgradeMs?: number;
  upgradeTo?: string[];
  upgradeEnds?: string;
}

let sessions: MockSession[] = [];
let assists: AssistRequest[] | null = null;
let seq = 0;

const iso = (ms: number) => new Date(ms).toISOString();

export function isRemoteMockPath(path: string): boolean {
  return /\/kmgr\/things\/[^/]+\/remote-sessions/.test(path) || /^\/v1\/orgs\/[^/]+\/kmgr\/assist-requests$/.test(path) || path === '/v1/me/kmgr/remote-sessions';
}

export function resetRemoteMock(): void {
  sessions = [];
  assists = null;
  seq = 0;
}

async function hostsOf(w: MockWorld, home: string): Promise<{ target_ref: string; hostname?: string }[]> {
  try {
    const r = (await kmgrMock.handle(w, 'GET', `/v1/homes/${encodeURIComponent(home)}/kmgr/targets`, { kind: 'agent' }, undefined)) as WireTargetList;
    return (r.targets ?? []).map((t) => ({
      target_ref: t.target_ref,
      hostname: t.hostname,
    }));
  } catch {
    return [];
  }
}

/** 推進時間：開始 1 秒 launched、4 秒 active；加權限 3 秒後允許。 */
function tick(m: MockSession, now = Date.now()): void {
  const s = m.s;
  if (m.lockedMs && s.state === 'requested' && now - m.lockedMs > 3000) {
    // 「解鎖」：Agent 自己跳允許視窗（awaiting_consent + console），倒數從這裡算
    m.startedMs = m.lockedMs + 3000;
    s.state = 'awaiting_consent';
    s.wait_unlock_until = undefined;
    m.events.push({ kind: 'unlocked', rev: s.rev, detail: {}, at: iso(m.startedMs) }, { kind: 'console', rev: s.rev, detail: { other_sessions: 1 }, at: iso(m.startedMs) });
    m.lockedMs = undefined;
  }
  if (m.startedMs && s.state === 'awaiting_consent') {
    if (now - m.startedMs > 1000 && !m.events.some((e) => e.kind === 'launched'))
      m.events.push({
        kind: 'launched',
        rev: s.rev,
        detail: {},
        at: iso(m.startedMs + 1000),
      });
    if (now - m.startedMs > 4000) {
      s.state = 'active';
      s.active_at = iso(m.startedMs + 4000);
      s.relay_kind = 4;
      m.events.push({
        kind: 'consent',
        rev: s.rev,
        detail: { permissions: s.permissions, screen_version: 'mock-1' },
        at: s.active_at,
      });
    }
  }
  if (m.upgradeMs && s.state === 'upgrade_pending' && now - m.upgradeMs > 3000) {
    const from = s.permissions;
    s.state = 'active';
    s.permissions = (m.upgradeTo ?? from) as RemoteSession['permissions'];
    if (m.upgradeEnds) s.ends_at = m.upgradeEnds;
    m.events.push({
      kind: 'permissions_changed',
      rev: s.rev,
      detail: { from, to: s.permissions },
      at: iso(m.upgradeMs + 3000),
    });
    m.upgradeMs = undefined;
  }
}

function end(m: MockSession, reason: string): void {
  const s = m.s;
  if (s.state === 'ended') return;
  const now = Date.now();
  s.state = 'ended';
  s.end_reason = reason;
  s.ended_at = iso(now);
  const dur = s.active_at ? Math.round((now - Date.parse(s.active_at)) / 1000) : 0;
  m.events.push({
    kind: 'ended',
    rev: s.rev,
    detail: { reason, duration_seconds: dur },
    at: s.ended_at,
  });
}

const copy = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export const remoteMock = {
  async handle(w: MockWorld, method: string, path: string, query: Record<string, string | number | string[] | undefined>, body: unknown): Promise<unknown> {
    const now = Date.now();
    sessions.forEach((m) => tick(m, now));
    const am = /^\/v1\/orgs\/([^/]+)\/kmgr\/assist-requests$/.exec(path);
    if (am) {
      if (!assists) {
        const home = w.homes.find((h) => h.org_id === am[1]);
        const hs = home ? await hostsOf(w, home.home_id) : [];
        const h = hs.find((x) => /DESKTOP|NB|WS/i.test(x.hostname ?? '')) ?? hs[0];
        assists =
          h && home
            ? [
                {
                  request_id: 'kar-mock-1',
                  org_id: am[1],
                  home_id: home.home_id,
                  device_ref: h.target_ref,
                  code: '482915',
                  note: '印表機一直顯示離線',
                  state: 'open',
                  expires_at: iso(now + 22 * 60_000),
                  created_at: iso(now - 8 * 60_000),
                },
              ]
            : [];
      }
      return {
        assist_requests: copy(assists.filter((a) => a.org_id === am[1] && a.state === 'open').map(({ org_id: _o, ...a }) => a)),
      };
    }
    if (path === '/v1/me/kmgr/remote-sessions') {
      const from = Date.parse(String(query.from ?? '1970-01-01T00:00:00Z'));
      const to = Date.parse(String(query.to ?? '2999-01-01T00:00:00Z'));
      const homes = new Set(w.homes.map((h) => h.home_id));
      const list = sessions.map((m) => m.s).filter((s) => homes.has(s.home_id) && Date.parse(s.created_at) >= from && Date.parse(s.created_at) < to);
      return {
        remote_sessions: copy(list.reverse().map((s) => ({ ...s, org_id: w.homes.find((h) => h.home_id === s.home_id)?.org_id }))),
        access: 'full',
        names: { users: w.me ? { [w.me]: w.meName ?? '' } : {} },
      };
    }
    const m = /^\/v1\/homes\/([^/]+)\/kmgr\/things\/([^/]+)\/remote-sessions(?:\/([^/]+))?(?:\/(start|upgrade|end|launch))?$/.exec(path);
    if (!m) throw new KinioError(404, 'NOT_FOUND', 'mock 沒有這個路徑');
    const [, home, device, sid, verb] = m.map((x) => (x === undefined ? x : decodeURIComponent(x)));
    if (!sid && method === 'GET')
      return {
        remote_sessions: copy(
          sessions
            .filter((x) => x.s.home_id === home && x.s.device_ref === device)
            .map((x) => x.s)
            .reverse(),
        ),
      };
    if (!sid && method === 'POST') {
      const b = body as {
        permissions: string[];
        reason?: string;
        assist_request_id?: string;
      };
      if (sessions.some((x) => x.s.device_ref === device && x.s.state !== 'ended'))
        throw new KinioError(409, 'CONFLICT', '這台已經有進行中的遠端協助', {
          reason: 'session_busy',
        });
      if (b.assist_request_id) {
        const a = assists?.find((x) => x.request_id === b.assist_request_id);
        if (a && a.state !== 'open')
          throw new KinioError(409, 'ASSIST_REQUEST_TAKEN', '已經有人接手', {
            request_id: a.request_id,
          });
        if (a) a.state = 'taken';
      }
      seq += 1;
      const id = `krs-MOCK${String(seq).padStart(4, '0')}`;
      const s: RemoteSession = {
        session_id: id,
        home_id: home,
        device_ref: device,
        mode: 'attended',
        operator: w.me,
        permissions: (b.permissions ?? ['view']) as RemoteSession['permissions'],
        rev: 1,
        state: 'requested',
        ticket_hash: 'A'.repeat(42) + String(seq % 10),
        ends_at: iso(now + 60 * 60_000),
        created_at: iso(now),
        reason: b.reason,
        assist_request_id: b.assist_request_id,
        recording: false,
      };
      sessions.push({
        s,
        events: [
          {
            kind: 'created',
            rev: 1,
            detail: { permissions: s.permissions },
            at: s.created_at,
          },
        ],
      });
      return {
        session_id: id,
        ticket_hash: s.ticket_hash,
        expires_at: iso(now + 5 * 60_000),
      };
    }
    const ms = sessions.find((x) => x.s.session_id === sid && x.s.home_id === home);
    if (!ms) throw new KinioError(404, 'NOT_FOUND', '找不到這次遠端協助');
    if (method === 'GET' && !verb) return { remote_session: copy(ms.s), events: copy(ms.events) };
    if (verb === 'start') {
      const hs = await hostsOf(w, home);
      const name = hs.find((h) => h.target_ref === device)?.hostname ?? '';
      if (/LOCK/i.test(name)) {
        ms.s.started_at = iso(now);
        ms.s.wait_unlock_until = iso(now + 4 * 60_000);
        ms.lockedMs = now;
        ms.events.push({ kind: 'started', rev: 1, detail: {}, at: ms.s.started_at }, { kind: 'waiting_unlock', rev: 1, detail: { until: ms.s.wait_unlock_until }, at: ms.s.started_at });
        throw new KinioError(403, 'REMOTE_NOT_ALLOWED', '螢幕鎖定中', {
          reason: 'locked',
          wait_unlock_until: ms.s.wait_unlock_until,
        });
      }
      if (/NOUSER/i.test(name)) {
        end(ms, 'rejected');
        throw new KinioError(403, 'REMOTE_NOT_ALLOWED', '沒有人登入', {
          reason: 'no_user',
        });
      }
      ms.s.state = 'awaiting_consent';
      ms.s.started_at = iso(now);
      ms.startedMs = now;
      ms.events.push({
        kind: 'started',
        rev: 1,
        detail: {},
        at: ms.s.started_at,
      });
      return {
        ticket: {},
        launch_url: `kinio-remote://open?h=mock.invalid&c=${ms.s.session_id}`,
        state: 'awaiting_consent',
      };
    }
    if (verb === 'launch') {
      if (ms.s.state === 'ended' || ms.s.state === 'requested') throw new KinioError(410, 'GONE', '已經不能開', { reason: 'state' });
      return { ticket: {}, launch_url: `kinio-remote://open?h=mock.invalid&c=${ms.s.session_id}-${now}`, state: ms.s.state };
    }
    if (verb === 'upgrade') {
      if (ms.s.state !== 'active')
        throw new KinioError(422, 'INVALID_TRANSITION', '不在進行中', {
          state: ms.s.state,
        });
      const b = body as { add_permissions?: string[]; extend?: boolean };
      ms.s.rev += 1;
      ms.s.state = 'upgrade_pending';
      ms.upgradeMs = now;
      ms.upgradeTo = [...new Set([...ms.s.permissions, ...(b.add_permissions ?? [])])];
      ms.upgradeEnds = b.extend ? iso(Date.parse(ms.s.ends_at) + 30 * 60_000) : undefined;
      ms.events.push({
        kind: 'upgraded',
        rev: ms.s.rev,
        detail: { permissions: ms.upgradeTo, extend: !!b.extend },
        at: iso(now),
      });
      return { session_id: sid, rev: ms.s.rev, state: 'upgrade_pending' };
    }
    if (verb === 'end') {
      const b = (body ?? {}) as { note?: string; external_ticket?: string };
      end(ms, 'operator_end');
      if (b.note) ms.s.note = b.note;
      if (b.external_ticket) ms.s.external_ticket = b.external_ticket;
      if (b.note || b.external_ticket)
        ms.events.push({
          kind: 'note',
          rev: ms.s.rev,
          detail: { ...b },
          at: iso(now),
        });
      return copy(ms.s);
    }
    throw new KinioError(404, 'NOT_FOUND', 'mock 沒有這個路徑');
  },
};
